import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Gear, CreditCard, ShieldCheck, Users, Broadcast, CurrencyEur, MusicNote,
  Vault, Eye, EyeSlash
} from "@phosphor-icons/react";

const StatCard = ({ icon: Icon, label, value, accent = "#E11D48", testId }) => (
  <div data-testid={testId} className="p-5 rounded-2xl border border-white/10 bg-[#0f0f10]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
      <Icon size={20} weight="duotone" style={{ color: accent }} />
    </div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ platform_name: "", platform_email: "", platform_rib: "", stripe_api_key: "" });
  const [showKey, setShowKey] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [vault, setVault] = useState([]);
  const [showVault, setShowVault] = useState(false);
  const [profile, setProfile] = useState({ full_name: "", current_password: "", new_password: "" });

  const refresh = async () => {
    const [s, st] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/settings"),
    ]);
    setStats(s.data);
    setSettings(st.data);
    setForm({
      platform_name: st.data.platform_name || "",
      platform_email: st.data.platform_email || "",
      platform_rib: st.data.platform_rib || "",
      stripe_api_key: "",
    });
  };

  useEffect(() => { if (user) { refresh(); setProfile({ full_name: user.full_name, current_password: "", new_password: "" }); } }, [user]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = { ...form };
      if (!payload.stripe_api_key) delete payload.stripe_api_key;
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      setForm((p) => ({ ...p, stripe_api_key: "" }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", {
        full_name: profile.full_name || undefined,
        current_password: profile.current_password || undefined,
        new_password: profile.new_password || undefined,
      });
      toast.success("Profil mis à jour");
      setProfile((p) => ({ ...p, current_password: "", new_password: "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingProfile(false);
    }
  };

  const loadVault = async () => {
    const { data } = await api.get("/admin/vault");
    setVault(data);
    setShowVault(true);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-2">Espace constructeur</p>
          <h1 className="font-display font-black text-4xl tracking-tight">Console Admin</h1>
          <p className="text-zinc-400 mt-2">Vue d'ensemble plateforme — paramètres, revenus, coffre fanbase.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard testId="admin-stat-users" icon={Users} label="Utilisateurs" value={stats?.total_users ?? "—"} />
          <StatCard testId="admin-stat-artists" icon={MusicNote} label="Artistes" value={stats?.total_artists ?? "—"} accent="#F59E0B" />
          <StatCard testId="admin-stat-live" icon={Broadcast} label="Lives actifs" value={stats?.live_now ?? "—"} />
          <StatCard testId="admin-stat-revenue" icon={CurrencyEur} label={`Marge plateforme (${stats?.platform_fee_percent || 10}%)`} value={stats ? `${stats.platform_revenue_eur}€` : "—"} accent="#10B981" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Stripe Settings */}
          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <div className="flex items-center gap-3 mb-5">
              <CreditCard size={24} className="text-[#E11D48]" weight="duotone" />
              <h2 className="font-display font-bold text-xl">Paiement & Stripe</h2>
            </div>
            <form onSubmit={saveSettings} className="space-y-4">
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Clé Stripe actuelle</Label>
                <div className="mt-2 p-3 rounded-lg bg-black/40 border border-white/5 font-mono text-sm flex items-center justify-between">
                  <span data-testid="admin-stripe-masked" className="text-[#10B981]">{settings?.stripe_api_key_masked || "—"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${settings?.stripe_configured_in_settings ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/10 text-zinc-400"}`}>
                    {settings?.stripe_configured_in_settings ? "Personnalisée" : "Système"}
                  </span>
                </div>
              </div>
              <div>
                <Label htmlFor="stripe" className="text-zinc-300">Nouvelle clé Stripe (sk_live_… ou sk_test_…)</Label>
                <div className="relative mt-2">
                  <Input
                    data-testid="admin-stripe-input"
                    id="stripe"
                    type={showKey ? "text" : "password"}
                    placeholder="sk_live_..."
                    value={form.stripe_api_key}
                    onChange={(e) => setForm({...form, stripe_api_key: e.target.value})}
                    className="bg-black/40 border-white/10 text-white font-mono pr-10"
                  />
                  <button type="button" onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    {showKey ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Récupérez votre clé sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#E11D48] hover:text-[#F59E0B]">dashboard.stripe.com/apikeys</a>
                </p>
              </div>
              <div>
                <Label htmlFor="prib" className="text-zinc-300">RIB / IBAN constructeur (vos revenus 10%)</Label>
                <Input data-testid="admin-prib" id="prib" placeholder="FR76..."
                  value={form.platform_rib}
                  onChange={(e) => setForm({...form, platform_rib: e.target.value})}
                  className="mt-2 bg-black/40 border-white/10 text-white font-mono text-sm" />
              </div>
              <Button data-testid="admin-save-settings" type="submit" disabled={savingSettings} className="w-full bg-[#E11D48] hover:bg-[#BE123C]">
                {savingSettings ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </form>
          </div>

          {/* Profile */}
          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck size={24} className="text-[#F59E0B]" weight="duotone" />
              <h2 className="font-display font-bold text-xl">Mon compte admin</h2>
            </div>
            <form onSubmit={saveProfile} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-zinc-300">Nom</Label>
                <Input data-testid="admin-name" id="name" value={profile.full_name}
                  onChange={(e) => setProfile({...profile, full_name: e.target.value})}
                  className="mt-2 bg-black/40 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input value={user.email} disabled className="mt-2 bg-black/20 border-white/5 text-zinc-500" />
              </div>
              <div>
                <Label htmlFor="cur" className="text-zinc-300">Mot de passe actuel (si changement)</Label>
                <Input data-testid="admin-cur-pwd" id="cur" type="password" value={profile.current_password}
                  onChange={(e) => setProfile({...profile, current_password: e.target.value})}
                  className="mt-2 bg-black/40 border-white/10 text-white" />
              </div>
              <div>
                <Label htmlFor="new" className="text-zinc-300">Nouveau mot de passe</Label>
                <Input data-testid="admin-new-pwd" id="new" type="password" value={profile.new_password}
                  onChange={(e) => setProfile({...profile, new_password: e.target.value})}
                  className="mt-2 bg-black/40 border-white/10 text-white" />
              </div>
              <Button data-testid="admin-save-profile" type="submit" disabled={savingProfile} className="w-full bg-[#E11D48] hover:bg-[#BE123C]">
                {savingProfile ? "Enregistrement..." : "Mettre à jour"}
              </Button>
            </form>
          </div>
        </div>

        {/* Vault */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#0f0f10] p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <Vault size={24} className="text-[#10B981]" weight="duotone" />
              <h2 className="font-display font-bold text-xl">Coffre fanbase ({stats?.vault_emails ?? 0} emails)</h2>
            </div>
            <Button data-testid="admin-load-vault" size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={loadVault}>
              {showVault ? "Rafraîchir" : "Ouvrir le coffre"}
            </Button>
          </div>
          {showVault && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/40">
                  <tr className="text-left text-xs uppercase tracking-widest text-zinc-500">
                    <th className="p-3">Email</th>
                    <th className="p-3">Nom</th>
                    <th className="p-3">Artistes suivis</th>
                    <th className="p-3">Concerts</th>
                    <th className="p-3">Dernière vue</th>
                  </tr>
                </thead>
                <tbody>
                  {vault.map((v) => (
                    <tr key={v.email} className="border-t border-white/5">
                      <td className="p-3 font-mono text-xs text-zinc-300">{v.email}</td>
                      <td className="p-3">{v.name}</td>
                      <td className="p-3">{v.artists?.length || 0}</td>
                      <td className="p-3">{v.concerts?.length || 0}</td>
                      <td className="p-3 text-zinc-400 text-xs">{new Date(v.last_seen).toLocaleDateString("fr-FR")}</td>
                    </tr>
                  ))}
                  {vault.length === 0 && (
                    <tr><td colSpan="5" className="p-6 text-center text-zinc-500">Coffre vide.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
